import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import styles from './styles.module.scss';
import ShowInfoGrid, { ShowInfoGridRow } from './ShowInfoGrid';
import { size } from '@/helpers/lodash';
import ConnectTilesHorizontalStack from '@/components/app/ConnectTiles/ConnectTilesHorizontalStack';
import Text from '@/components/component-library/Text';
import { classes } from '@/helpers/styling';
import ButtonCTA from '@/components/component-library/Button/CTA';
import { getSupportedPlatforms, getTicketsPlatform } from '@/helpers/supported-platforms';
import ButtonTextWithCaret from '@/components/component-library/Button/TextWithCaret';
import GenreBadges from '@/components/component-library/GenreBadges';

const MAX_DESCRIPTION_LINES = 5;

const getLineCount = (element) => {
  if (!element) {
    return 0;
  }

  const lineHeight = getComputedStyle(element)?.getPropertyValue('line-height')?.replace('px', '') || 1;
  const height = getComputedStyle(element)?.getPropertyValue('height')?.replace('px', '') || 0;

  const lineCount = Math.ceil(height / lineHeight);
  
  return lineCount;
};

const HostCard = ({
  hosts,
  from,
  genres,
  description,
  className,
  loading,
  links,
  releaseYear
}) => {
  const descriptionRef = useRef(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(true);
  const buyTicketsLinkObject = useMemo(() => getTicketsPlatform({ links }) || {}, [links]);
  const buyTicketsLinkUrl = buyTicketsLinkObject.url;

  const handleExpandDescription = useCallback(() => setDescriptionExpanded(true), []);

  useEffect(() => {
    const lineCount = getLineCount(descriptionRef.current);

    if (lineCount > MAX_DESCRIPTION_LINES) {
      setDescriptionExpanded(false);
    }
  }, [description]);

  const supportedPlatformsCount = useMemo(() => size(getSupportedPlatforms({ links })), [links]);

  const linksRendered = useMemo(() => (supportedPlatformsCount ? (
    <ConnectTilesHorizontalStack
      alignLeft
      className={ styles.connectTiles }
      hideTitle
      links={ links }
      loading={ loading }
    />
  ) : null), [links, loading, supportedPlatformsCount]);

  return (
    <div className={ classes(styles.hostCard, className) }>
      { loading ? (
        <>
          <Text className={ styles.descriptionLoading } loading />
          <Text className={ styles.descriptionLoading } loading />
          <Text className={ styles.descriptionLoading } loading />
          <Text className={ classes(styles.descriptionLoading, styles.isBottomRow) } loading />
        </>
      ) : (
        <>
          <Text
            className={ styles.description }
            maximumLineCount={ descriptionExpanded ? null : MAX_DESCRIPTION_LINES }
            ref={ descriptionRef }
          >
            { description }
          </Text>
          { !descriptionExpanded && <ButtonTextWithCaret className={ styles.learnMoreButton } onClick={ handleExpandDescription }>Learn More</ButtonTextWithCaret> }
        </>
      ) }
      <ShowInfoGrid>
        <ShowInfoGridRow
          isLoading={ loading }
          renderedValue={ (
            <GenreBadges
              enableLinks
              genres={ genres }
              isVerticallyCentered
              loading={ loading }
            />
          ) }
          shouldDisplay={ loading || !!size(genres) }
          title="Genres"
        />
        <ShowInfoGridRow
          isLoading={ loading }
          shouldDisplay={ loading || !!size(hosts) }
          textValue={ (hosts || []).join(', ') }
          title={`Hosts (${size(hosts)})`}
        />
        <ShowInfoGridRow
          isLoading={ loading }
          shouldDisplay={ loading || !!releaseYear }
          textValue={ releaseYear }
          title="Released"
        />
        <ShowInfoGridRow
          isLoading={ loading }
          shouldDisplay={ loading || !!from }
          textValue={ from }
          title="From"
        />
        <ShowInfoGridRow
          isLoading={ loading }
          renderedValue={ linksRendered }
          shouldDisplay={ loading || !!supportedPlatformsCount }
          title="Socials"
        />
      </ShowInfoGrid>
      { buyTicketsLinkUrl && (
        <ButtonCTA
          className={ styles.buyTicketsButton }
          href={ buyTicketsLinkUrl }
          isRectangular
          loading={ loading }
          openInNewTab
        >
          Buy Tickets
        </ButtonCTA>
      ) }
    </div>
  );
};

HostCard.propTypes = {
  hosts: PropTypes.arrayOf(PropTypes.string),
  from: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  loading: PropTypes.bool,
  links: PropTypes.array,
  genres: PropTypes.array,
  releaseYear: PropTypes.number
};

HostCard.defaultProps = {
  hosts: null,
  from: null,
  description: '',
  className: '',
  loading: false,
  links: [],
  genres: [],
  releaseYear: undefined
};

export default memo(HostCard);
